import Store from 'localforage'
import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, race, select, take, takeLatest } from 'redux-saga/effects'

import { actions, selectors, types } from '../reducers/auth'
import { authenticatedPostRequest } from './network'
import { apolloClient } from '@fe/business/src/apollo'
import { TOKEN_KEY } from '../config'

import * as rolesReducer from '../reducers/roles'
import { actions as clubsActions } from '../reducers/clubs'
import { actions as coursesActions } from '../reducers/courses'
import { actions as teesActions } from '../reducers/tees'
import { actions as guidesActions } from '../reducers/guides'
import { navigateReplaceRoot } from './redirects'
import i18n from '../i18n'

export { TOKEN_KEY }

const USER_FIELDS = gql`
  fragment UserFields on User {
    id
    firstName
    lastName
    email
    admin
    language
    clubId
    roles {
      clubId
      roles
    }
  }
`

// Selectors expect roles as { [clubId]: [roles] }
const withRolesMap = user => ({
  ...user,
  roles: _.flow(_.keyBy('clubId'), _.mapValues('roles'))(user.roles || [])
})

const LOGIN_MUTATION = gql`
  ${USER_FIELDS}
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        ...UserFields
      }
    }
  }
`

const ME_QUERY = gql`
  ${USER_FIELDS}
  query Me {
    me {
      ...UserFields
    }
  }
`

const INIT_PASSWORD_RESET_MUTATION = gql`
  mutation InitPasswordReset($email: String!) {
    initPasswordReset(email: $email)
  }
`

const CONFIRM_PASSWORD_RESET_MUTATION = gql`
  mutation ConfirmPasswordReset($email: String!, $token: String!, $password: String!) {
    confirmPasswordReset(email: $email, token: $token, password: $password)
  }
`

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loginEffect ({ email, password }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: LOGIN_MUTATION,
      variables: { email, password }
    })
    const { token, user } = _.get('data.login', response)
    return { token, ...withRolesMap(user) }
  } catch (error) {
    return { error }
  }
}

export function * authenticatedEffect () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: ME_QUERY,
      fetchPolicy: 'network-only'
    })
    const user = _.get('data.me', response)
    return user ? withRolesMap(user) : { error: { status: 401, statusText: 'Unauthorized' } }
  } catch (error) {
    return { error }
  }
}

export function * logoutEffect () {
  yield call(clearToken)
}

export function * renewSessionEffect () {
  const url = 'token'
  const token = yield select(selectors.getToken)
  const request = yield call(authenticatedPostRequest, token)
  const { error, expired_at: expiredAt } = yield call(request, url)
  return error
    ? { error }
    : { token, expired_at: expiredAt }
}

export function * clearToken () {
  yield Store.clear()
}

export function * saveToken (tokenObject) {
  yield Store.setItem(TOKEN_KEY, tokenObject)
}

export function * loadToken () {
  return yield Store.getItem(TOKEN_KEY)
}

export function * initResetPassword (email) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: INIT_PASSWORD_RESET_MUTATION,
      variables: { email }
    })
    return { success: _.get('data.initPasswordReset', response) }
  } catch (error) {
    return { error }
  }
}

export function * resetPassword ({ password, email, token }) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: CONFIRM_PASSWORD_RESET_MUTATION,
      variables: { email, token, password }
    })
    const success = _.get('data.confirmPasswordReset', response)
    return success ? { success } : { error: 'Invalid or expired reset link' }
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

/**
 * takeLatest(types.LOGIN_REQUEST, loginFlow)
 */
export function * loginFlow ({ payload }) {
  const first = yield race({
    login: call(loginEffect, payload),
    logout: take(types.LOGOUT)
  })
  yield put(actions.loginRequestDone())

  const nextPathName = yield select(_.get('router.location.pathname'))
  if (first.login) {
    const { error, ...loginResponse } = first.login
    if (error) {
      yield put(actions.loginError({ error }))
    } else {
      yield put(actions.setToken({ token: loginResponse.token }))
      yield call(saveToken, loginResponse)
      yield put(rolesReducer.actions.loadRequest())
      // wait for response
      yield take([rolesReducer.types.LOAD_SUCCESS, rolesReducer.types.LOAD_ERROR])
      // don't care if roles were retrieved
      yield put(actions.loginSuccess({ ...loginResponse, nextPathName }))
    }
  } else if (first.logout) {
    yield call(logoutFlow)
  }
}

export function * bootstrapFlow () {
  const nextPathName = yield select(_.get('router.location.pathname'))
  const tokenObject = yield call(loadToken)

  if (tokenObject) {
    const { token } = tokenObject
    yield put(actions.setToken({ token }))
    const userOrError = yield call(authenticatedEffect)

    if (userOrError.error) {
      yield put(actions.logout({ nextPathName }))
    } else {
      yield put(rolesReducer.actions.loadRequest())
      // wait for response
      yield take([rolesReducer.types.LOAD_SUCCESS, rolesReducer.types.LOAD_ERROR])
      // don't care if roles were retrieved
      yield put(actions.loginSuccess({ ...userOrError, token, nextPathName }))
    }
  } else {
    yield put(actions.logout({ nextPathName }))
  }
  yield put(actions.bootstrapDone())
}

/**
 * takeLatest(types.LOGOUT, logoutFlow)
 */
export function * logoutFlow () {
  const token = yield select(selectors.getToken)
  yield [
    call(logoutEffect, token),
    call(clearToken)
  ]
}

/**
 * takeLatest(types.RENEW_SESSION_REQUEST, renewSessionFlow)
 */
export function * renewSessionFlow () {
  const { token, expired_at: expiredAt, error } = yield call(renewSessionEffect)
  if (error) {
    yield put(actions.logout())
  } else {
    yield put(actions.renewSessionSuccess({ token, expired_at: expiredAt }))
  }
}

export function * getClubResources () {
  yield put(clubsActions.loadRequestSingle())
  yield put(coursesActions.loadRequest())
  yield put(teesActions.loadRequest())
  yield put(guidesActions.loadRequest())
}

export function * initResetPasswordFlow ({ payload }) {
  return yield call(initResetPassword, payload)
}

export function * resetPasswordFlow ({ payload }) {
  const result = yield call(resetPassword, payload)
  if (result.error) {
    yield put(actions.passwordResetError(result.error))
  } else {
    yield put(actions.passwordResetSuccess(true))
    yield call(navigateReplaceRoot, '/signin')
  }
  yield put(actions.passwordResetDone())
}

function * reloadProfileFlow () {
  const result = yield call(authenticatedEffect)
  if (result.error) {
    yield put(actions.reloadProfileError(result.error))
  } else {
    yield put(actions.reloadProfileSuccess(result))
  }
}

function * setLanguageFlow ({payload}) {
  i18n.changeLanguage(_.get('language', payload))
  i18n.reloadResources() // fetch languages from server
}

export default function * () {
  return yield all([
    takeLatest(types.LOGIN_REQUEST, loginFlow),
    takeLatest(types.LOGOUT, logoutFlow),
    takeLatest(types.RENEW_SESSION_REQUEST, renewSessionFlow),
    takeLatest(types.BOOTSTRAP_REQUEST, bootstrapFlow),
    takeLatest(types.INIT_PASSWORD_RESET, initResetPasswordFlow),
    takeLatest(types.PASSWORD_RESET, resetPasswordFlow),
    takeLatest(types.RELOAD_PROFILE_REQUEST, reloadProfileFlow),
    takeLatest(types.LOGIN_SUCCESS, setLanguageFlow),
    takeLatest(types.RELOAD_PROFILE_SUCCESS, setLanguageFlow)
  ])
}
